import { API_ENDPOINTS } from '@tsgecart/config';
import { adminApiFetch } from '../../api/admin-client.js';
import type {
  AdminBrand,
  AdminCategory,
  AdminProductDetail,
  AdminProductListItem,
  AdminTaxRate,
  AdminVariant,
  BulkProductAction,
  BulkProductActionResult,
  ProductListParams,
} from '../types/catalog.js';

export interface FetchOptions {
  token: string;
  signal?: AbortSignal;
}

function buildQuery(params: Record<string, string | number | boolean | string[] | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------

export async function fetchAdminProducts(
  params: ProductListParams,
  options: FetchOptions,
): Promise<AdminProductListItem[]> {
  const query = buildQuery({
    search: params.search,
    categoryId: params.categoryId,
    brandId: params.brandId,
    status: params.status,
    isFeatured: params.isFeatured,
    inStockOnly: params.inStockOnly,
    sortField: params.sortField,
    sortDirection: params.sortDirection,
    limit: params.limit,
    offset: params.offset,
  });
  return adminApiFetch<AdminProductListItem[]>(`${API_ENDPOINTS.adminProducts}${query}`, options);
}

export async function fetchAdminProduct(id: string, options: FetchOptions): Promise<AdminProductDetail> {
  return adminApiFetch<AdminProductDetail>(`${API_ENDPOINTS.adminProducts}/${id}`, options);
}

export interface CreateProductInput {
  name: string;
  slug?: string;
  categoryId: string;
  brandId?: string | null;
  taxRateId: string;
  description?: string | null;
  status?: string;
  isFeatured?: boolean;
  metaTitle?: string | null;
  metaDescription?: string | null;
  canonicalUrl?: string | null;
  ogImageMediaAssetId?: string | null;
}

export async function createAdminProduct(
  input: CreateProductInput,
  options: FetchOptions,
): Promise<AdminProductDetail> {
  return adminApiFetch<AdminProductDetail>(API_ENDPOINTS.adminProducts, {
    ...options,
    method: 'POST',
    body: input,
  });
}

export type UpdateProductInput = Partial<CreateProductInput>;

export async function updateAdminProduct(
  id: string,
  input: UpdateProductInput,
  options: FetchOptions,
): Promise<AdminProductDetail> {
  return adminApiFetch<AdminProductDetail>(`${API_ENDPOINTS.adminProducts}/${id}`, {
    ...options,
    method: 'PATCH',
    body: input,
  });
}

export async function archiveAdminProduct(id: string, options: FetchOptions): Promise<AdminProductDetail> {
  return adminApiFetch<AdminProductDetail>(`${API_ENDPOINTS.adminProducts}/${id}/archive`, {
    ...options,
    method: 'POST',
  });
}

export async function activateAdminProduct(id: string, options: FetchOptions): Promise<AdminProductDetail> {
  return adminApiFetch<AdminProductDetail>(`${API_ENDPOINTS.adminProducts}/${id}/activate`, {
    ...options,
    method: 'POST',
  });
}

export async function bulkUpdateAdminProducts(
  input: BulkProductAction,
  options: FetchOptions,
): Promise<BulkProductActionResult> {
  return adminApiFetch<BulkProductActionResult>(`${API_ENDPOINTS.adminProducts}/bulk`, {
    ...options,
    method: 'POST',
    body: input,
  });
}

// ---------------------------------------------------------------------------
// Variants
// ---------------------------------------------------------------------------

export async function fetchAdminVariants(productId: string, options: FetchOptions): Promise<AdminVariant[]> {
  return adminApiFetch<AdminVariant[]>(`${API_ENDPOINTS.adminProducts}/${productId}/variants`, options);
}

export interface CreateVariantInput {
  sku?: string;
  variantName: string;
  unitOfMeasure: string;
  netQuantity: string;
  packSize?: number;
  isOrganic?: boolean;
  isPerishable?: boolean;
  mrpPaise: number;
  sellingPricePaise: number;
  costPricePaise?: number | null;
  barcode?: string | null;
  weightGrams?: number | null;
  lengthMm?: number | null;
  widthMm?: number | null;
  heightMm?: number | null;
  isActive?: boolean;
}

export async function createAdminVariant(
  productId: string,
  input: CreateVariantInput,
  options: FetchOptions,
): Promise<AdminVariant> {
  return adminApiFetch<AdminVariant>(`${API_ENDPOINTS.adminProducts}/${productId}/variants`, {
    ...options,
    method: 'POST',
    body: input,
  });
}

export type UpdateVariantInput = Partial<CreateVariantInput>;

export async function updateAdminVariant(
  variantId: string,
  input: UpdateVariantInput,
  options: FetchOptions,
): Promise<AdminVariant> {
  return adminApiFetch<AdminVariant>(`${API_ENDPOINTS.adminVariants}/${variantId}`, {
    ...options,
    method: 'PATCH',
    body: input,
  });
}

export async function activateAdminVariant(variantId: string, options: FetchOptions): Promise<AdminVariant> {
  return adminApiFetch<AdminVariant>(`${API_ENDPOINTS.adminVariants}/${variantId}/activate`, {
    ...options,
    method: 'POST',
  });
}

export async function deactivateAdminVariant(
  variantId: string,
  options: FetchOptions,
): Promise<AdminVariant> {
  return adminApiFetch<AdminVariant>(`${API_ENDPOINTS.adminVariants}/${variantId}/deactivate`, {
    ...options,
    method: 'POST',
  });
}

// ---------------------------------------------------------------------------
// Tax rates (read-only reference data)
// ---------------------------------------------------------------------------

export async function fetchAdminTaxRates(options: FetchOptions): Promise<AdminTaxRate[]> {
  return adminApiFetch<AdminTaxRate[]>(API_ENDPOINTS.adminTaxRates, options);
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export async function fetchAdminCategories(
  params: { parentId?: string; isActive?: boolean; limit?: number; offset?: number },
  options: FetchOptions,
): Promise<AdminCategory[]> {
  const query = buildQuery(params);
  return adminApiFetch<AdminCategory[]>(`${API_ENDPOINTS.adminCategories}${query}`, options);
}

export interface CreateCategoryInput {
  name: string;
  slug?: string;
  parentId?: string | null;
  description?: string | null;
  sortOrder?: number;
  isActive?: boolean;
  imageMediaAssetId?: string | null;
  bannerMediaAssetId?: string | null;
  isFeatured?: boolean;
  seoTitle?: string | null;
  seoMetaDescription?: string | null;
}

export async function createAdminCategory(
  input: CreateCategoryInput,
  options: FetchOptions,
): Promise<AdminCategory> {
  return adminApiFetch<AdminCategory>(API_ENDPOINTS.adminCategories, {
    ...options,
    method: 'POST',
    body: input,
  });
}

export type UpdateCategoryInput = Partial<CreateCategoryInput>;

export async function updateAdminCategory(
  id: string,
  input: UpdateCategoryInput,
  options: FetchOptions,
): Promise<AdminCategory> {
  return adminApiFetch<AdminCategory>(`${API_ENDPOINTS.adminCategories}/${id}`, {
    ...options,
    method: 'PATCH',
    body: input,
  });
}

export async function deactivateAdminCategory(id: string, options: FetchOptions): Promise<AdminCategory> {
  return adminApiFetch<AdminCategory>(`${API_ENDPOINTS.adminCategories}/${id}/deactivate`, {
    ...options,
    method: 'POST',
  });
}

export async function activateAdminCategory(id: string, options: FetchOptions): Promise<AdminCategory> {
  return adminApiFetch<AdminCategory>(`${API_ENDPOINTS.adminCategories}/${id}/activate`, {
    ...options,
    method: 'POST',
  });
}

// ---------------------------------------------------------------------------
// Brands
// ---------------------------------------------------------------------------

export async function fetchAdminBrands(
  params: { isActive?: boolean; limit?: number; offset?: number },
  options: FetchOptions,
): Promise<AdminBrand[]> {
  const query = buildQuery(params);
  return adminApiFetch<AdminBrand[]>(`${API_ENDPOINTS.adminBrands}${query}`, options);
}

export interface CreateBrandInput {
  name: string;
  slug?: string;
  logoMediaAssetId?: string | null;
  bannerMediaAssetId?: string | null;
  description?: string | null;
  manufacturer?: string | null;
  countryOfOrigin?: string | null;
  supplier?: string | null;
  website?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  seoTitle?: string | null;
  seoMetaDescription?: string | null;
  isActive?: boolean;
}

export async function createAdminBrand(input: CreateBrandInput, options: FetchOptions): Promise<AdminBrand> {
  return adminApiFetch<AdminBrand>(API_ENDPOINTS.adminBrands, { ...options, method: 'POST', body: input });
}

export type UpdateBrandInput = Partial<CreateBrandInput>;

export async function updateAdminBrand(
  id: string,
  input: UpdateBrandInput,
  options: FetchOptions,
): Promise<AdminBrand> {
  return adminApiFetch<AdminBrand>(`${API_ENDPOINTS.adminBrands}/${id}`, {
    ...options,
    method: 'PATCH',
    body: input,
  });
}

export async function activateAdminBrand(id: string, options: FetchOptions): Promise<AdminBrand> {
  return adminApiFetch<AdminBrand>(`${API_ENDPOINTS.adminBrands}/${id}/activate`, {
    ...options,
    method: 'POST',
  });
}

export async function deactivateAdminBrand(id: string, options: FetchOptions): Promise<AdminBrand> {
  return adminApiFetch<AdminBrand>(`${API_ENDPOINTS.adminBrands}/${id}/deactivate`, {
    ...options,
    method: 'POST',
  });
}
